import { useState } from 'react';
import {
  Modal,
  Tabs,
  Stack,
  Group,
  Text,
  Badge,
  Code,
  Table,
  Paper,
  Button,
  CopyButton,
  Alert,
  Divider,
  ScrollArea,
  Box,
  Accordion,
  List,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCode,
  IconCopy,
  IconCheck,
  IconWorld,
  IconPuzzle,
  IconSend,
  IconAppWindow,
  IconInfoCircle,
  IconPlayerPlay,
  IconTerminal2,
  IconBook,
  IconSparkles,
} from '@tabler/icons-react';

interface ExtensionDevGuideModalProps {
  opened: boolean;
  onClose: () => void;
  defaultTab?: 'iframe' | 'slot';
  sampleSlotId?: string;
}

export function ExtensionDevGuideModal({
  opened,
  onClose,
  defaultTab = 'iframe',
  sampleSlotId = 'sample_slot',
}: ExtensionDevGuideModalProps) {
  const [activeTab, setActiveTab] = useState<string | null>(defaultTab);

  // Sample Custom HTML Code
  const sampleCustomHtmlCode = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>CakeChat Custom Extension</title>
  <style>
    :root {
      --primary-color: #3b82f6;
      --bg-color: #ffffff;
      --text-color: #1e293b;
      --card-bg: #f8fafc;
      --border-color: #e2e8f0;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      background: transparent;
      color: var(--text-color);
      padding: 12px;
      user-select: none;
    }
    .panel-card {
      background: var(--bg-color);
      border: 1px solid var(--border-color);
      border-radius: 12px;
      padding: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }
    .title {
      font-size: 14px;
      font-weight: 600;
    }
    .status-pill {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 999px;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .status-idle { background: #e0f2fe; color: #0284c7; }
    .status-sleep { background: #ede9fe; color: #7c3aed; }
    .status-thinking { background: #fef3c7; color: #d97706; }
    .status-streaming { background: #dcfce7; color: #16a34a; }
    .status-error { background: #fee2e2; color: #dc2626; }
    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    .quick-chips {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-bottom: 12px;
    }
    .chip-btn {
      background: var(--card-bg);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 12px;
      text-align: left;
      cursor: pointer;
      transition: all 0.15s ease;
      color: var(--text-color);
    }
    .chip-btn:hover {
      background: #eff6ff;
      border-color: #bfdbfe;
      color: #1d4ed8;
      transform: translateY(-1px);
    }

    .input-row {
      display: flex;
      gap: 6px;
      margin-bottom: 10px;
    }
    .text-input {
      flex: 1;
      padding: 6px 10px;
      border: 1px solid var(--border-color);
      border-radius: 6px;
      font-size: 12px;
      outline: none;
    }
    .text-input:focus {
      border-color: var(--primary-color);
    }
    .send-btn {
      background: var(--primary-color);
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 0 12px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
    }

    .window-controls {
      display: flex;
      gap: 6px;
    }
    .control-btn {
      flex: 1;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 6px;
      font-size: 11px;
      cursor: pointer;
      color: #475569;
    }
    .control-btn:hover {
      background: #e2e8f0;
    }
  </style>
</head>
<body>

  <div class="panel-card">
    <div class="header">
      <span class="title">✨ 快捷小幫手</span>
      <span id="botStateBadge" class="status-pill status-idle">
        <span class="status-dot"></span>
        <span id="botStateText">待機 (Idle)</span>
      </span>
    </div>

    <!-- 快捷發問清單 -->
    <div class="quick-chips">
      <button class="chip-btn" onclick="CakeChat.sendMessage('推薦總價 1500 萬以內的三房建案')">
        🏡 推薦 1500 萬內三房建案
      </button>
      <button class="chip-btn" onclick="CakeChat.sendMessage('目前青年安心成家貸款利率是多少？')">
        💰 查詢最新房貸優惠利率
      </button>
      <button class="chip-btn" onclick="CakeChat.sendMessage('我想預約這週末現場專人賞屋')">
        📅 預約週末現場賞屋
      </button>
    </div>

    <!-- 自訂輸入送出 -->
    <div class="input-row">
      <input id="customInput" class="text-input" type="text" placeholder="輸入問題送至聊天室..." />
      <button class="send-btn" onclick="handleSendCustom()">發送</button>
    </div>

    <!-- 控制聊天視窗開關 -->
    <div class="window-controls">
      <button class="control-btn" onclick="CakeChat.toggle()">切換聊天室</button>
      <button class="control-btn" onclick="CakeChat.open()">開啟</button>
      <button class="control-btn" onclick="CakeChat.close()">關閉</button>
    </div>
  </div>

  <script>
    /**
     * CakeChat 輕量通訊 SDK
     */
    const CakeChat = {
      // 發送使用者問題至聊天室
      sendMessage: function(text) {
        if (!text || !text.trim()) return;
        window.parent.postMessage({
          type: 'cakechat:send-message',
          payload: { text: text.trim() }
        }, '*');
      },
      // 開啟聊天室
      open: function() {
        window.parent.postMessage({ type: 'cakechat:open' }, '*');
      },
      // 關閉聊天室
      close: function() {
        window.parent.postMessage({ type: 'cakechat:close' }, '*');
      },
      // 切換開關
      toggle: function() {
        window.parent.postMessage({ type: 'cakechat:toggle' }, '*');
      },
      // 喚醒待機 / 重置無互動倒數
      wake: function() {
        window.parent.postMessage({ type: 'cakechat:wake' }, '*');
      },
      // 註冊機器人狀態變更回呼
      onStateChange: function(callback) {
        window.addEventListener('message', function(e) {
          if (e.data && e.data.type === 'cakechat:state-change') {
            callback(e.data.payload);
          }
        });
      }
    };

    // 發送自訂輸入框內容
    function handleSendCustom() {
      const input = document.getElementById('customInput');
      if (input && input.value) {
        CakeChat.sendMessage(input.value);
        input.value = '';
      }
    }

    // 支援 Enter 鍵送出
    document.getElementById('customInput').addEventListener('keydown', function(e) {
      if (e.key === 'Enter') handleSendCustom();
    });

    // 監聽並反映機器人即時狀態
    CakeChat.onStateChange(function(payload) {
      const badge = document.getElementById('botStateBadge');
      const text = document.getElementById('botStateText');
      if (!badge || !text) return;

      badge.className = 'status-pill';
      if (payload.state === 'sleep') {
        badge.classList.add('status-sleep');
        text.innerText = '💤 休眠中 (Sleep)';
      } else if (payload.state === 'thinking') {
        badge.classList.add('status-thinking');
        text.innerText = '思考中...';
      } else if (payload.state === 'streaming') {
        badge.classList.add('status-streaming');
        text.innerText = '回答中...';
      } else if (payload.state === 'error') {
        badge.classList.add('status-error');
        text.innerText = '發生錯誤';
      } else {
        badge.classList.add('status-idle');
        text.innerText = '待機中 (Idle)';
      }
    });
  </script>
</body>
</html>`;

  // Sample extension.json
  const sampleExtensionJsonCode = `{
  "version": "1.0",
  "template": "custom-html",
  "entry": "index.html",
  "settings": {
    "width": "280px",
    "height": "380px",
    "backgroundColor": "transparent"
  }
}`;

  // Markdown documentation for LLM (Iframe Extension)
  const iframePostMessageMarkdown = `# CakeChat Iframe Extension API Specification

This document provides the bidirectional PostMessage communication protocol specification for developing custom HTML extensions inside CakeChat's sandbox iframe.

## Overview
Custom extensions run in an isolated iframe. Communication between the extension and the CakeChat host is handled via \`window.parent.postMessage\` and window message event listeners.

## PostMessage API Specification

| Direction | Message Type (\`type\`) | Payload (\`payload\`) | Description |
| :--- | :--- | :--- | :--- |
| **Iframe → CakeChat** | \`cakechat:send-message\` | \`{ text: string }\` | Send a user message to the AI chatbot to initiate or continue a conversation. |
| **Iframe → CakeChat** | \`cakechat:open\` | \`{}\` (empty object) | Open / expand the chat widget window. |
| **Iframe → CakeChat** | \`cakechat:close\` | \`{}\` (empty object) | Close / collapse the chat widget window. |
| **Iframe → CakeChat** | \`cakechat:toggle\` | \`{}\` (empty object) | Toggle the chat widget window open/close state. |
| **Iframe → CakeChat** | \`cakechat:wake\` | \`{}\` (empty object) | Wake the chatbot companion to \`idle\` and reset the inactivity countdown. |
| **CakeChat → Iframe** | \`cakechat:state-change\` | \`{ state: "idle" \\| "sleep" \\| "thinking" \\| "streaming" \\| "error", isThinking: boolean, isStreaming: boolean }\` | Broadcasted when the AI chatbot state changes. |

## Lightweight JavaScript SDK Helper

Embed this helper in your \`index.html\` to communicate with CakeChat easily:

\`\`\`javascript
const CakeChat = {
  // Send message to chatbot
  sendMessage: function(text) {
    if (!text || !text.trim()) return;
    window.parent.postMessage({
      type: 'cakechat:send-message',
      payload: { text: text.trim() }
    }, '*');
  },
  // Open chat window
  open: function() {
    window.parent.postMessage({ type: 'cakechat:open' }, '*');
  },
  // Close chat window
  close: function() {
    window.parent.postMessage({ type: 'cakechat:close' }, '*');
  },
  // Toggle chat window
  toggle: function() {
    window.parent.postMessage({ type: 'cakechat:toggle' }, '*');
  },
  // Wake companion from sleep to idle
  wake: function() {
    window.parent.postMessage({ type: 'cakechat:wake' }, '*');
  },
  // Listen for bot state changes (idle, sleep, thinking, streaming, error)
  onStateChange: function(callback) {
    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'cakechat:state-change') {
        callback(e.data.payload);
      }
    });
  }
};
\`\`\`

## extension.json Manifest

Place this configuration file at the root of the ZIP asset bundle:

\`\`\`json
{
  "version": "1.0",
  "template": "custom-html",
  "entry": "index.html",
  "settings": {
    "width": "280px",
    "height": "380px",
    "backgroundColor": "transparent"
  }
}
\`\`\``;

  // Markdown documentation for LLM (Web Component Slot)
  const slotIntegrationMarkdown = `# CakeChat Web Component Slot Integration API Specification

This document provides the DOM API and CustomEvent specification for integrating CakeChat into host web applications using the \`<cakechat-widget>\` Web Component and custom slots.

## Slot Mounting Rule
Mount custom HTML elements or components into extension zones using the \`slot\` attribute:

\`\`\`html
<cakechat-widget bot-provider-id="YOUR_BOT_PROVIDER_ID" host="https://your-domain.com">
  <!-- Mount to the designated slot (e.g., slotId: "${sampleSlotId}") -->
  <div slot="extension-${sampleSlotId}">
    <!-- Custom UI content -->
  </div>
</cakechat-widget>
\`\`\`

## Host DOM APIs & CustomEvents

| Name | Kind | Parameters / \`detail\` | Description |
| :--- | :--- | :--- | :--- |
| \`widget.sendMessage(text)\` | Method | \`text: string\` | Sends a message to the chatbot as the user. |
| \`widget.open()\` / \`close()\` / \`toggle()\` | Method | none | Opens, closes, or toggles the chat widget window. |
| \`widget.wake()\` | Method | none | Wakes the chatbot companion from \`sleep\` to \`idle\` and resets inactivity timer. |
| \`widget.getState()\` | Method | none (Returns \`{ isOpen, state, isProcessing, isStreaming, isSleeping, error }\`) | Gets the current widget status. |
| \`cakechat:state-change\` | CustomEvent | \`{ state, previousState, isThinking, isStreaming }\` | Fired when chatbot state changes (\`idle\`, \`sleep\`, \`thinking\`, \`streaming\`, \`error\`). |
| \`cakechat:open-change\` | CustomEvent | \`{ isOpen: boolean }\` | Fired when chat widget is opened or closed. |
| \`cakechat:message-sent\` | CustomEvent | \`{ text, timestamp }\` | Fired when a user sends a message. |
| \`cakechat:message-received\` | CustomEvent | \`{ text, role, isComplete, historyId }\` | Fired when a chatbot response chunk or message is received. |

## Example Usage

\`\`\`javascript
const widget = document.getElementById('myChatWidget');

// Send message to chatbot
widget.sendMessage('Hello');

// Listen to bot state changes
widget.addEventListener('cakechat:state-change', (e) => {
  console.log('Bot state:', e.detail.state);
});
\`\`\``;

  // Sample Web Component Slot Integration
  const sampleSlotIntegrationHtml = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8">
  <title>CakeChat Web Component Slot 整合測試</title>
  <style>
    body { font-family: sans-serif; padding: 24px; }
    .custom-slot-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      width: 220px;
    }
    .custom-slot-card button {
      display: block;
      width: 100%;
      margin-top: 6px;
      padding: 6px 8px;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      cursor: pointer;
      text-align: left;
      font-size: 12px;
    }
    .custom-slot-card button:hover {
      background: #e2e8f0;
    }
  </style>
</head>
<body>

  <h1>網站主頁面</h1>
  <p>下方為嵌入之 CakeChat Web Component，並透過 Slot 注入自訂 HTML 元素：</p>

  <!-- 引入 ChatWidget 腳本 -->
  <script src="/Resources/chat-widget.js"></script>

  <!-- Web Component 標籤 -->
  <cakechat-widget
    id="myChatWidget"
    bot-provider-id="YOUR_BOT_PROVIDER_ID"
    host="https://your-domain.com"
  >
    <!-- 透過 slot="extension-{slotId}" 注入自訂元素 -->
    <div slot="extension-${sampleSlotId}" class="custom-slot-card">
      <h4 style="margin: 0 0 6px 0; font-size: 13px;">🏢 外部 Slot 擴充區</h4>
      <p style="font-size: 11px; color: #64748b; margin: 0 0 8px 0;">這是直接由宿主頁面 DOM 注入的內容</p>
      <button onclick="sendToBot('請列出近期新開案名單')">📌 新開案名單</button>
      <button onclick="sendToBot('預約本週專人服務')">📞 專人聯繫</button>
    </div>
  </cakechat-widget>

  <script>
    const widget = document.getElementById('myChatWidget');

    // 1. 透過 DOM API 發送訊息
    function sendToBot(message) {
      if (widget && widget.sendMessage) {
        widget.sendMessage(message);
      }
    }

    // 2. 監聽 Widget 事件
    widget.addEventListener('cakechat:state-change', (e) => {
      console.log('機器人狀態改變:', e.detail);
      // e.detail: { state: 'idle'|'thinking'|'streaming'|'error', isThinking, isStreaming }
    });

    widget.addEventListener('cakechat:open-change', (e) => {
      console.log('聊天室開關狀態:', e.detail.isOpen);
    });

    widget.addEventListener('cakechat:message-sent', (e) => {
      console.log('使用者發送訊息:', e.detail.text);
    });

    widget.addEventListener('cakechat:message-received', (e) => {
      console.log('收到機器人回覆:', e.detail.text, '是否完成:', e.detail.isComplete);
    });
  </script>
</body>
</html>`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconBook size={20} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="md">
            擴充區 (Extension) 與自訂網頁開發指南
          </Text>
        </Group>
      }
      size="xl"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab
            value="iframe"
            leftSection={<IconWorld size={16} />}
          >
            自訂網頁 / Iframe 擴充
          </Tabs.Tab>
          <Tabs.Tab
            value="slot"
            leftSection={<IconPuzzle size={16} />}
          >
            Web Component Slot 外部掛載
          </Tabs.Tab>
        </Tabs.List>

        {/* Tab 1: Iframe Custom HTML Extension */}
        <Tabs.Panel value="iframe">
          <Stack gap="md">
            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              將自行撰寫的靜態網頁（HTML/CSS/JS）打包上傳至資產包中，並於 <Code>extension.json</Code> 設定為 <Code>custom-html</Code> 模板。聊天室開啟時會自動以沙盒 Iframe 載入該頁面，並透過 <Code>postMessage</Code> 進行即時雙向通訊。
            </Alert>

            {/* PostMessage API Reference */}
            <Paper withBorder p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm">
                  📡 PostMessage 雙向通訊 API 規格
                </Text>
                <CopyButton value={iframePostMessageMarkdown}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : 'blue'}
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製' : '複製LLM優化文件'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Table withTableBorder withColumnBorders verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 140 }}>方向</Table.Th>
                    <Table.Th style={{ width: 220 }}>訊息類型 (type)</Table.Th>
                    <Table.Th>資料內容 (payload)</Table.Th>
                    <Table.Th>說明</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="blue" size="xs" variant="light">
                        Iframe → 聊天室
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:send-message</Code>
                    </Table.Td>
                    <Table.Td>
                      <Code>{`{ text: string }`}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">觸發聊天室送出訊息給 AI 機器人進行對話</Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="blue" size="xs" variant="light">
                        Iframe → 聊天室
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:open</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">無 (空物件)</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">主動展開/開啟聊天室視窗</Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="blue" size="xs" variant="light">
                        Iframe → 聊天室
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:close</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">無 (空物件)</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">收合/關閉聊天室視窗</Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="blue" size="xs" variant="light">
                        Iframe → 聊天室
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:toggle</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">無 (空物件)</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">切換聊天室視窗開關狀態</Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="blue" size="xs" variant="light">
                        Iframe → 聊天室
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:wake</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">無 (空物件)</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">喚醒機器人為待機狀態 (idle) 並重置無互動倒數計時器</Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge color="teal" size="xs" variant="light">
                        聊天室 → Iframe
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Code fw={600}>cakechat:state-change</Code>
                    </Table.Td>
                    <Table.Td>
                      <Code>{`{ state, isThinking, isStreaming }`}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        機器人狀態改變廣播。state 可為 <Code>idle</Code> (待機)、<Code>sleep</Code> (休眠)、<Code>thinking</Code> (思考中)、<Code>streaming</Code> (回答中)、<Code>error</Code> (錯誤)
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>

            {/* Ready-to-use index.html sample */}
            <Paper withBorder p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <div>
                  <Text fw={600} size="sm">
                    📄 開箱即用範本：index.html (含輕量 SDK & 快捷互動)
                  </Text>
                  <Text size="xs" c="dimmed">
                    可直接複製並存為 <Code>index.html</Code> 上傳至資產包中即可運行
                  </Text>
                </div>
                <CopyButton value={sampleCustomHtmlCode}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : 'blue'}
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製 index.html' : '複製 index.html 範本'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <ScrollArea.Autosize mah={260}>
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                  {sampleCustomHtmlCode}
                </Code>
              </ScrollArea.Autosize>
            </Paper>

            {/* Sample extension.json */}
            <Paper withBorder p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <div>
                  <Text fw={600} size="sm">
                    ⚙️ Manifest 範本：extension.json
                  </Text>
                  <Text size="xs" c="dimmed">
                    資產包根目錄下的設定檔，指定範本類型為 custom-html
                  </Text>
                </div>
                <CopyButton value={sampleExtensionJsonCode}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : 'blue'}
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製 extension.json' : '複製 extension.json'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Code block style={{ fontSize: '11px' }}>
                {sampleExtensionJsonCode}
              </Code>
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Tab 2: Web Component Slot External Injection */}
        <Tabs.Panel value="slot">
          <Stack gap="md">
            <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light">
              如果不想透過資產包 Iframe 載入，亦可直接在宿主網頁中利用 Web Component Slot 機制，將任何 HTML 元素或 React/Vue 元件注入至 ChatWidget 四周對應的擴充區域。
            </Alert>

            {/* Slot Naming Rule */}
            <Paper withBorder p="sm" radius="md">
              <Text fw={600} size="sm" mb="xs">
                🧩 Slot 命名與掛載規則
              </Text>
              <Stack gap="xs">
                <Text size="xs">
                  在後台「擴充區設定」中建立的每一個擴充區皆有一個專屬的 <Code>ID</Code>。在前端頁面中，只需在要掛載的標籤上加入 <Code>slot="extension-{`{ID}`}"</Code> 屬性：
                </Text>
                <Code block style={{ fontSize: '12px' }}>
                  {`<cakechat-widget bot-provider-id="YOUR_ID" host="https://your-domain.com">
  <!-- 掛載至指定的擴充區 (例如 slotId 為 "${sampleSlotId}") -->
  <div slot="extension-${sampleSlotId}">
    <!-- 自訂 UI 內容 -->
  </div>
</cakechat-widget>`}
                </Code>
              </Stack>
            </Paper>

            {/* Host DOM Imperative Methods & Events */}
            <Paper withBorder p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm">
                  ⚡ 宿主頁面 DOM API 與 CustomEvent 監聽清單
                </Text>
                <CopyButton value={slotIntegrationMarkdown}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : 'blue'}
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製' : '複製LLM優化文件'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Table withTableBorder withColumnBorders verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 180 }}>API / 事件名稱</Table.Th>
                    <Table.Th style={{ width: 100 }}>類型</Table.Th>
                    <Table.Th>參數 / detail</Table.Th>
                    <Table.Th>說明</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>widget.sendMessage(text)</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="indigo">Method</Badge></Table.Td>
                    <Table.Td><Code>text: string</Code></Table.Td>
                    <Table.Td><Text size="xs">以使用者身分送出訊息至聊天室</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>widget.open() / close() / toggle()</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="indigo">Method</Badge></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">無</Text></Table.Td>
                    <Table.Td><Text size="xs">開啟、關閉或切換聊天室視窗</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>widget.wake()</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="indigo">Method</Badge></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">無</Text></Table.Td>
                    <Table.Td><Text size="xs">喚醒機器人為待機狀態 (idle) 並重置無互動倒數計時器</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>widget.getState()</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="indigo">Method</Badge></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">無</Text></Table.Td>
                    <Table.Td><Text size="xs">取得目前狀態 <Code>{`{ isOpen, state, isProcessing, isStreaming, isSleeping, error }`}</Code></Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>cakechat:state-change</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="teal">Event</Badge></Table.Td>
                    <Table.Td><Code>{`{ state, previousState, isThinking, isStreaming }`}</Code></Table.Td>
                    <Table.Td><Text size="xs">當機器人狀態變更時觸發（idle / sleep / thinking / streaming / error）</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>cakechat:open-change</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="teal">Event</Badge></Table.Td>
                    <Table.Td><Code>{`{ isOpen: boolean }`}</Code></Table.Td>
                    <Table.Td><Text size="xs">聊天室開啟或關閉時觸發</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>cakechat:message-sent</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="teal">Event</Badge></Table.Td>
                    <Table.Td><Code>{`{ text, timestamp }`}</Code></Table.Td>
                    <Table.Td><Text size="xs">使用者發送訊息時觸發</Text></Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td><Code fw={600}>cakechat:message-received</Code></Table.Td>
                    <Table.Td><Badge size="xs" color="teal">Event</Badge></Table.Td>
                    <Table.Td><Code>{`{ text, role, isComplete, historyId }`}</Code></Table.Td>
                    <Table.Td><Text size="xs">機器人回答產生或更新時觸發</Text></Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>

            {/* Single HTML Test Page */}
            <Paper withBorder p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <div>
                  <Text fw={600} size="sm">
                    🌐 可直接運行的完整 Single HTML 測試網頁
                  </Text>
                  <Text size="xs" c="dimmed">
                    可儲存為 <Code>test.html</Code> 直接於瀏覽器中執行與除錯
                  </Text>
                </div>
                <CopyButton value={sampleSlotIntegrationHtml}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : 'teal'}
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? '已複製 HTML 範例' : '複製完整測試 HTML'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <ScrollArea.Autosize mah={260}>
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                  {sampleSlotIntegrationHtml}
                </Code>
              </ScrollArea.Autosize>
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>
          關閉指南
        </Button>
      </Group>
    </Modal>
  );
}
